'use client';

import { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useTranslations } from 'next-intl';
import CmsImage from '@/components/ui/CmsImage';
import { getImageUrl } from '@/lib/homepage-mappers';
import { MEDIA_PLACEHOLDER } from '@/lib/media-placeholder';
import { getCmsButtonLinkProps } from '@/lib/cms-button-target';
import {
  getStrapiOriginalMediaUrl,
  getStrapiPdfDownloadUrl,
  isStrapiVideoMedia,
} from '@/lib/strapi-media-url';
import type { TransformedSustainabilityData } from '@/types/sustainability';
import CmsRichTextHeading from '@/components/ui/CmsRichTextHeading';

const HERO_BG = '/images/sustainability.png';

function DownloadIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        d="M12 3v12m0 0l4-4m-4 4l-4-4M4 21h16"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

interface SustainabilityHeroEnquiryProps {
  hero?: TransformedSustainabilityData['hero'];
}

export default function SustainabilityHeroEnquiry({ hero }: SustainabilityHeroEnquiryProps) {
  const t = useTranslations('sustainability.hero');
  const sectionRef = useRef<HTMLElement | null>(null);
  const fixedImageRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!sectionRef.current || !fixedImageRef.current) return;
    if (!window.matchMedia('(min-width: 1024px)').matches) {
      gsap.set(fixedImageRef.current, { opacity: 1, scale: 1 });
      return;
    }

    gsap.registerPlugin(ScrollTrigger);

    const ctx = gsap.context(() => {
      gsap.set(fixedImageRef.current, { opacity: 1, scale: 1 });
      gsap.to(fixedImageRef.current, {
        opacity: 0,
        scale: 1.06,
        transformOrigin: 'top center',
        ease: 'none',
        scrollTrigger: {
          trigger: sectionRef.current,
          start: '-13% top',
          end: '40% top',
          scrub: 1,
        },
      });
    }, sectionRef);

    return () => {
      ctx.revert();
    };
  }, []);

  const cmsTitle = hero?.pageTitle?.trim();
  const cmsBgUrl = getStrapiOriginalMediaUrl(hero?.bannerMedia as never);
  const cmsCtaText = hero?.downloadButton?.buttonText?.trim();
  const cmsCtaHref = getImageUrl(hero?.downloadButton?.buttonMedia as never);
  const cmsCtaAria = hero?.downloadButton?.ariaLabel ?? undefined;

  const heroBg = cmsBgUrl || HERO_BG;
  const isHeroVideo = isStrapiVideoMedia(hero?.bannerMedia as never);
  const headlineText = cmsTitle || t('headline');
  const ctaText = cmsCtaText || t('cta');
  const reportHref = getStrapiPdfDownloadUrl(cmsCtaHref || t('ctaHref')) || '#';

  const ctaClassName =
    'inline-flex min-h-[46px] w-full items-center justify-center gap-2 rounded-lg bg-red-600 px-5 py-3 text-sm font-medium text-white shadow-lg transition-colors hover:bg-red-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-500 sm:w-auto md:min-h-[48px] md:text-base';
  const heroMediaProps = {
    src: heroBg,
    media: isHeroVideo ? (hero?.bannerMedia as never) : null,
    alt: t('imageAlt'),
    fill: true as const,
    priority: true,
    loading: 'eager' as const,
    className: 'object-cover object-top',
    sizes: '100vw',
    posterSrc: MEDIA_PLACEHOLDER,
    fallbackSrc: MEDIA_PLACEHOLDER,
  };

  return (
    <section ref={sectionRef} className="relative w-full overflow-hidden bg-black">
      {/* Mobile / Tablet: image with content anchored to bottom */}
      <div className="relative bg-black lg:hidden">
        <div className="relative min-h-[52svh] w-full sm:min-h-[58svh]">
          <CmsImage {...heroMediaProps} />
          <div
            aria-hidden
            className="absolute inset-0 bg-gradient-to-t from-black via-black/55 to-black/20"
          />
        </div>
        <div className="absolute inset-x-0 bottom-0 z-10 px-4 py-10 sm:px-6 sm:py-12 md:px-8">
          <div className="mx-auto max-w-[640px]">
            <CmsRichTextHeading
              className="hero-title text-white"
              html={hero?.titleRichText}
              fallback={headlineText}
            />
            <div className="mt-5 sm:mt-6">
              <a
                href={reportHref}
                {...getCmsButtonLinkProps(hero?.downloadButton)}
                aria-label={cmsCtaAria}
                className={ctaClassName}
                download
              >
                <span>{ctaText}</span>
                <DownloadIcon className="shrink-0" />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Desktop: GSAP fixed image with overlaid content */}
      <div className="hidden lg:block">
        <div ref={fixedImageRef} className="fixed left-0 top-0 z-0 h-[calc(100vh-125px)] w-full">
          <CmsImage {...heroMediaProps} />
        </div>
        <div className="relative h-[clamp(560px,88vh,900px)] w-full">
          <div className="relative h-full w-full overflow-hidden">
            <div className="relative h-full w-full">
              <div className="pointer-events-none absolute inset-0 z-30 bg-black/10" />
              <div
                className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_top,rgba(0,0,0,1)_0%,rgba(0,0,0,0.55)_22%,rgba(0,0,0,0.2)_42%,transparent_72%)]"
                aria-hidden
              />

              <div className="absolute inset-0 z-10 flex flex-col justify-end">
                <div className="container mx-auto py-12 md:py-14 lg:py-16">
                  <div className="max-w-[660px]">
                    <CmsRichTextHeading
                      className="hero-title text-white"
                      html={hero?.titleRichText}
                      fallback={headlineText}
                    />
                    <div className="mt-8">
                      <a
                        href={reportHref}
                        {...getCmsButtonLinkProps(hero?.downloadButton)}
                        aria-label={cmsCtaAria}
                        className={ctaClassName}
                        download
                      >
                        <span>{ctaText}</span>
                        <DownloadIcon className="shrink-0" />
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
